import { useEffect, useState } from "react";
import { getUserStats, type UserRow, type UserStats } from "../lib/api";

interface Props {
  user: UserRow;
  teacherName: string;
  onStart: () => void;
}

function levelProgress(level: string | null): number {
  const order = ["A1", "A2", "B1", "B2", "C1", "C2"];
  const idx = level ? order.indexOf(level) : -1;
  if (idx < 0) return 0;
  return Math.round(((idx + 1) / order.length) * 100);
}

export function Dashboard({ user, teacherName, onStart }: Props): JSX.Element {
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getUserStats()
      .then((s) => setStats(s))
      .catch(() => {
        /* non-blocking */
      })
      .finally(() => setLoading(false));
  }, []);

  const lvl = user.level ?? "—";
  const pct = levelProgress(user.level);
  const greeting = user.name ? `Hi, ${user.name}` : "Welcome back";

  return (
    <div className="h-full overflow-y-auto bg-gradient-to-br from-ink-900 via-ink-800 to-ink-900">
      <div className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        <header>
          <h1 className="text-2xl font-semibold text-white">{greeting}</h1>
          <p className="text-sm text-ink-400 mt-1">
            Ready for a lesson with {teacherName}?
          </p>
        </header>

        <section className="rounded-2xl border border-ink-700 bg-ink-900/60 p-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-ink-400">
                Your level
              </div>
              <div className="text-4xl font-semibold text-white mt-1">{lvl}</div>
            </div>
            <button
              onClick={onStart}
              className="rounded-xl bg-lime-500 hover:bg-lime-400 text-ink-900 font-semibold px-5 py-3 shadow-lg shadow-lime-500/20"
            >
              ▶ Start lesson
            </button>
          </div>
          <div className="mt-4 h-2 rounded-full bg-ink-800 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-lime-500 to-lime-300"
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="flex justify-between text-[10px] text-ink-500 mt-1 uppercase tracking-widest">
            <span>A1</span>
            <span>A2</span>
            <span>B1</span>
            <span>B2</span>
            <span>C1</span>
            <span>C2</span>
          </div>
        </section>

        <section className="grid grid-cols-3 gap-3">
          <Stat
            label="Sessions"
            value={loading ? "…" : String(stats?.total_sessions ?? 0)}
          />
          <Stat
            label="Your turns"
            value={loading ? "…" : String(stats?.total_turns_user ?? 0)}
          />
          <Stat
            label="Errors logged"
            value={loading ? "…" : String(stats?.total_errors ?? 0)}
          />
        </section>

        <section className="rounded-2xl border border-ink-700 bg-ink-900/60 p-5">
          <h2 className="text-xs uppercase tracking-widest text-ink-400 mb-3">
            Things to focus on
          </h2>
          {loading ? (
            <p className="text-sm text-ink-500">Loading…</p>
          ) : stats && stats.top_recurring_errors.length > 0 ? (
            <ul className="space-y-2">
              {stats.top_recurring_errors.map((e, i) => (
                <li
                  key={i}
                  className="rounded-lg border border-ink-700 bg-ink-800/60 px-3 py-2 text-sm"
                >
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-white">
                      {e.category.replace(/-/g, " ")}
                    </div>
                    <div className="font-mono text-[11px] text-ink-400">
                      {e.type} · ×{e.count}
                    </div>
                  </div>
                  <div className="text-xs text-ink-400 mt-1">{e.explanation}</div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-ink-400">
              No data yet — start your first session and we'll begin tracking what to practice.
            </p>
          )}
        </section>

        <section className="text-xs text-ink-500">
          <span>Goal: </span>
          <span className="text-ink-300">{user.goal ?? "—"}</span>
          <span className="mx-2">·</span>
          <span>Native: </span>
          <span className="text-ink-300">{user.native_lang}</span>
          <span className="mx-2">·</span>
          <span>Learning: </span>
          <span className="text-ink-300">{user.target_lang}</span>
        </section>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="rounded-xl border border-ink-700 bg-ink-900/60 px-4 py-3">
      <div className="text-2xl font-semibold text-white">{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-ink-400 mt-1">
        {label}
      </div>
    </div>
  );
}
